import { useState, useRef, useEffect, useCallback } from "react";
import { cn } from "@/lib/utils";
import { Star, ChevronLeft, ChevronRight, Quote } from "lucide-react";

const testimonials = [
  {
    name: "Marie L.",
    role: "Designer Freelance, Paris",
    avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop",
    content: "Floop a divisé mon temps de création par 10. Mes clients pensent que j'ai embauché une équipe. En réalité, c'est juste moi et Floop !",
    rating: 5,
  },
  {
    name: "Thomas K.",
    role: "Créateur de contenu, Lyon",
    avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop",
    content: "3 posts Instagram par jour avec des visuels uniques ? Impossible avant Floop. Maintenant c'est ma routine quotidienne.",
    rating: 5,
  },
  {
    name: "Sophie M.",
    role: "Directrice Marketing, Dakar",
    avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop",
    content: "Notre budget design a baissé de 60% et notre output a triplé. Floop comprend vraiment ce qu'on veut créer.",
    rating: 5,
  },
  {
    name: "Jean-Pierre D.",
    role: "E-commerçant, Douala",
    avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop",
    content: "J'ai créé toutes les photos produits de ma boutique en 2 heures. Avant, ça me coûtait 500€ et 2 semaines d'attente.",
    rating: 5,
  },
  {
    name: "Aminata B.",
    role: "Illustratrice, Abidjan",
    avatar: "https://images.unsplash.com/photo-1489424731084-a5d8b219a5bb?w=100&h=100&fit=crop",
    content: "Je l'utilise pour mes brainstormings. Floop génère des concepts en 10 secondes que je peux ensuite affiner. Game changer !",
    rating: 5,
  },
  {
    name: "Lucas R.",
    role: "Game Designer, Montréal",
    avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop",
    content: "Les concept arts Floop Pro sont bluffants. Mon équipe valide les directions artistiques 5x plus vite maintenant.",
    rating: 5,
  },
  {
    name: "Chloé V.",
    role: "Social Media Manager, Bruxelles",
    avatar: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&h=100&fit=crop",
    content: "Gérer 8 comptes clients avec des visuels uniques chaque jour ? Floop rend ça possible. Je ne pourrais plus m'en passer.",
    rating: 5,
  },
  {
    name: "Moussa T.",
    role: "Photographe, Yaoundé",
    avatar: "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=100&h=100&fit=crop",
    content: "Floop Pro pour la retouche, c'est magique. Mes clients ne voient pas la différence avec un travail manuel de 2 heures.",
    rating: 5,
  },
];

const TestimonialCard = ({ testimonial, isActive }: { testimonial: typeof testimonials[0]; isActive: boolean }) => {
  return (
    <div className={cn(
      "flex-shrink-0 w-[300px] md:w-[340px] p-5 rounded-2xl transition-all duration-500 ease-out",
      "glass-card border border-border/30",
      isActive ? "scale-100 opacity-100 border-primary/30" : "scale-95 opacity-50"
    )}>
      <div className="flex items-start gap-3 mb-3">
        <img
          src={testimonial.avatar}
          alt={testimonial.name}
          className="w-11 h-11 rounded-full object-cover ring-2 ring-primary/30"
          loading="lazy"
        />
        <div>
          <p className="font-semibold text-foreground text-sm">{testimonial.name}</p>
          <p className="text-xs text-muted-foreground">{testimonial.role}</p>
          <div className="flex gap-0.5 mt-0.5">
            {[...Array(testimonial.rating)].map((_, i) => (
              <Star key={i} className="w-3 h-3 text-highlight fill-highlight" />
            ))}
          </div>
        </div>
      </div>
      
      <div className="relative">
        <Quote className="absolute -top-1 -left-1 w-6 h-6 text-primary/20" />
        <p className="text-muted-foreground leading-relaxed pl-3 text-sm">
          {testimonial.content}
        </p>
      </div>
    </div>
  );
};

export const TestimonialsSection = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const handlePrev = useCallback(() => {
    setActiveIndex(prev => (prev > 0 ? prev - 1 : testimonials.length - 1));
  }, []);

  const handleNext = useCallback(() => {
    setActiveIndex(prev => (prev < testimonials.length - 1 ? prev + 1 : 0));
  }, []);

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setStartX(e.pageX - (scrollRef.current?.offsetLeft || 0));
    setScrollLeft(scrollRef.current?.scrollLeft || 0);
    setIsPaused(true);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !scrollRef.current) return;
    e.preventDefault();
    const x = e.pageX - (scrollRef.current.offsetLeft || 0);
    const walk = (x - startX) * 2;
    scrollRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setTimeout(() => setIsPaused(false), 2000);
  };

  // Smooth scroll to active testimonial
  useEffect(() => {
    if (scrollRef.current) {
      const cardWidth = 356;
      scrollRef.current.scrollTo({
        left: activeIndex * cardWidth - (scrollRef.current.clientWidth - 340) / 2,
        behavior: 'smooth'
      });
    }
  }, [activeIndex]);

  // Auto-scroll every 4 seconds
  useEffect(() => {
    if (isPaused) return;
    
    const interval = setInterval(() => {
      handleNext();
    }, 4000);
    
    return () => clearInterval(interval);
  }, [handleNext, isPaused]);

  return (
    <section className="py-20 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 mesh-gradient-bg opacity-20" />
      
      <div className="container mx-auto px-6 relative z-10">
        <div className="text-center mb-10">
          <span className="inline-flex items-center gap-2 glass-card border border-primary/30 rounded-full px-4 py-2 mb-4 text-sm font-medium text-foreground">
            <Star className="w-4 h-4 text-highlight fill-highlight" />
            Témoignages
          </span>
          
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-4 font-display">
            +2 800 créateurs nous font{" "}
            <span className="gradient-text-animated">confiance</span>
          </h2>
          
          <p className="text-base text-muted-foreground max-w-xl mx-auto">
            Designers, marketeurs, entrepreneurs... ils ont transformé leur workflow avec Floop.
          </p>
        </div>

        {/* Carousel */}
        <div 
          className="relative"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          <div
            ref={scrollRef}
            className="flex gap-4 overflow-x-auto scrollbar-hide pb-4 cursor-grab active:cursor-grabbing"
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            <div className="flex-shrink-0 w-[calc((100vw-340px)/2)]" />
            {testimonials.map((testimonial, index) => (
              <TestimonialCard 
                key={index} 
                testimonial={testimonial} 
                isActive={index === activeIndex}
              />
            ))}
            <div className="flex-shrink-0 w-[calc((100vw-340px)/2)]" />
          </div>

          {/* Navigation */}
          <div className="flex justify-center items-center gap-3 mt-6">
            <button
              onClick={() => { handlePrev(); setIsPaused(true); setTimeout(() => setIsPaused(false), 3000); }}
              className="w-10 h-10 rounded-full glass-card border border-border/30 flex items-center justify-center text-foreground hover:border-primary/50 hover:text-primary transition-all duration-300"
              aria-label="Previous testimonial"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            
            <div className="flex gap-1">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => { setActiveIndex(index); setIsPaused(true); setTimeout(() => setIsPaused(false), 3000); }}
                  aria-label={`Go to testimonial ${index + 1}`}
                  className="w-6 h-6 flex items-center justify-center"
                >
                  <span
                    className={cn(
                      "rounded-full transition-all duration-300",
                      index === activeIndex
                        ? "w-6 h-1.5 bg-gradient-floop"
                        : "w-1.5 h-1.5 bg-muted-foreground/30 hover:bg-muted-foreground/50"
                    )}
                  />
                </button>
              ))}
            </div>
            
            <button
              onClick={() => { handleNext(); setIsPaused(true); setTimeout(() => setIsPaused(false), 3000); }}
              className="w-10 h-10 rounded-full glass-card border border-border/30 flex items-center justify-center text-foreground hover:border-primary/50 hover:text-primary transition-all duration-300"
              aria-label="Next testimonial"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};